<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    location="bottom end"
    offset="8"
  >
    <template v-slot:activator="{ props }">
      <v-btn
        v-bind="props"
        icon
        variant="text"
        class="notification-bell"
        :class="{ 'has-unread': unreadCount > 0 }"
      >
        <v-badge
          :content="unreadCount"
          :model-value="unreadCount > 0"
          color="error"
          overlap
          class="notification-badge"
        >
          <v-icon
            :icon="unreadCount > 0 ? 'mdi-bell-ring' : 'mdi-bell-outline'"
            :class="{ 'bell-ring': unreadCount > 0 }"
          />
        </v-badge>
      </v-btn>
    </template>

    <NotificationDropdown />
  </v-menu>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNotificationStore } from '@/store/notifications'
import NotificationDropdown from './NotificationDropdown.vue'

const notificationStore = useNotificationStore()
const menuOpen = ref(false)
let pollingInterval: number | null = null

// Computed properties
const unreadCount = computed(() => notificationStore.unreadCount)

// Fetch notifications on mount
onMounted(async () => {
  try {
    await notificationStore.fetchNotifications({ page: 1, page_size: 20 })
  } catch (error) {
    console.error('Failed to fetch notifications:', error)
  }

  // Set up polling every 30 seconds
  startPolling()
})

// Clear polling on unmount
onUnmounted(() => {
  stopPolling()
})

// Methods
const startPolling = () => {
  // Poll for new notifications every 30 seconds
  pollingInterval = window.setInterval(async () => {
    try {
      await notificationStore.fetchNotifications({ page: 1, page_size: 20 })
    } catch (error) {
      console.error('Failed to poll notifications:', error)
    }
  }, 30000) // 30 seconds
}

const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval)
    pollingInterval = null
  }
}
</script>

<style scoped lang="scss">
.notification-bell {
  position: relative;

  &.has-unread {
    .v-icon {
      color: rgb(102, 126, 234);
    }
  }
}

.notification-badge {
  :deep(.v-badge__badge) {
    font-size: 0.65rem;
    font-weight: 700;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
  }
}

.bell-ring {
  animation: ring 2s ease-in-out infinite;
}

@keyframes ring {
  0% {
    transform: rotate(0deg);
  }
  5% {
    transform: rotate(15deg);
  }
  10% {
    transform: rotate(-15deg);
  }
  15% {
    transform: rotate(10deg);
  }
  20% {
    transform: rotate(-10deg);
  }
  25% {
    transform: rotate(5deg);
  }
  30% {
    transform: rotate(-5deg);
  }
  35% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

@media (max-width: 600px) {
  .notification-bell {
    :deep(.v-btn__content) {
      font-size: 1.2rem;
    }
  }
}
</style>
